import { jwtDecode } from 'jwt-decode';
import React, { FormEvent, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import FormSolicitacao from './formSolicitacao';
import logoSisdaf from '../../../assets/logo_sisaf_negativo.png';
import capaSisdaf from '../../../assets/solicitacao_vinculo.png';
import Button from '../../../components/Button';
import ContainerBase from '../../../components/ContainerBase';
import { CustomSwal } from '../../../components/CustomFeedback/CustomSwal';
import CustomInputMask from '../../../components/CustomInputs/CustomInputMask';
import Input from '../../../components/InputNew';
import { getUser } from '../../../modules/contexts/Auth/RequireAuth';
import { getDominiosSelect } from '../../../services/dominio/dominioService';
import {
  checkPendingRequest,
  createUsuarioVinculo,
  validarUsuarioVinculo,
} from '../../../services/usuario/usuarioVinculoService';
import { validarForm } from '../../../utils/formValidar';
import {
  getDominioOptions,
  transformarEnumOptionValueEmLabel,
} from '../../../utils/functions';
import GlobalStyle from '../../../utils/globalStyles';

const ContainerSolicitacao = styled.div`
  display: grid;
  grid-template-columns: 70% 30%;
  margin: 0 auto;
  align-self: center;
  width: 85%;
  height: 75%;
  border-radius: 15px;
`;

const SecaoForm = styled.div`
  display: grid;
  background-color: #fff;
  border-radius: 20px 0 0 20px;
  grid-template-rows: 10% 80%;
`;

const SecaoImagem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5vh;
  background: url(${capaSisdaf}) no-repeat center;
  background-size: cover;
  min-height: 100%;
  border-radius: 0 20px 20px 0;
  align-items: center;
  justify-content: start;
  color: white;

  img {
    max-height: 20vh;
    margin-top: 25%;
  }

  div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  span {
    font-weight: bold;
    font-size: 3.5vh;
    text-align: center;
  }

  p {
    font-weight: normal;
    font-size: 2.5vh;
    width: 80%;
    line-height: 1.2;
    text-align: center;
  }
`;

const IconLogoContainer = styled.div`
  display: grid;
  grid-template-columns: 45% 55%;
  width: 100%;
  align-items: center;

  a {
    display: flex;
    align-self: center;
    text-decoration: none;
    color: ${({ theme }) => theme.colors.botaoLaranja};
    font-size: 12px;
    padding: 0.25rem;
    margin-left: 2rem;
  }
  img {
    height: 2.5vh;
    max-height: 50px;
    width: auto;
  }
`;

const Title = styled.h3`
  color: ${({ theme }) => theme.colors.textoAzul};
  text-align: center;
  margin-bottom: 1rem;
  font-weight: bold;
  font-size: 1.5rem;
`;

const FormContainer = styled(Form)`
  align-self: center;
  justify-content: center;
  width: 50%;
  margin: 0 auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 2rem;

  button {
    width: 25%;
  }
`;

const SolicitarVinculo: React.FC = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [solicitacaoData, setSolicitacaoData] = useState({
    nome: '',
    nuInterface: '',
    papelId: '',
    numeroSeiContrato: '',
    numeroDocumentoSEI: '',
    interfaceVinculo: '',
    interfaceId: '',
    observacao: '',
  });

  const [formData, setFormData] = useState({
    cpf: '',
    senha: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  const voltar = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    navigate('/', { replace: true });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    if (step === 2) {
      try {
        const user = getUser();
        const userID = user.sub;

        const dominios = JSON.parse(
          localStorage.getItem('dominios_select') || '[]',
        );

        // Encontrar ID do status 'Pendente' para stSolicitacao
        const dominioPendente = dominios.find(
          (d: any) => d.nome === 'Pendente' && d.dominioPai === 145,
        );

        const optionsInterface = getDominioOptions(97);
        const interfaceLabel = transformarEnumOptionValueEmLabel(
          Number(solicitacaoData.nuInterface),
          optionsInterface,
        );
        const camposObrigatorios = {
          nuInterface: 'Interface',
          papelId: 'Papel Usuário',
          interfaceVinculo: 'Setor DAF, SES ou Fornecedor',
          ...(interfaceLabel === 'Fornecedor'
            ? { numeroSeiContrato: 'Nº Processo SEI' }
            : {}),
          ...(interfaceLabel === 'Fornecedor'
            ? { numeroDocumentoSEI: 'Documento SEI' }
            : {}),
        };
        const valido = validarForm(solicitacaoData, camposObrigatorios);
        if (!valido) {
          setSubmitting(false);
          return false;
        }
        const payload = {
          usuarioId: userID,
          status: 0,
          interfaceId: Number(solicitacaoData.interfaceId),
          nuInterface: Number(solicitacaoData.nuInterface),
          interface: interfaceLabel,
          papelId: Number(solicitacaoData.papelId),
          nuSolicitacao: 1,
          stSolicitacao: dominioPendente.id,
          dtSolicitacao: new Date(),
          dataInicio: new Date(),
          numeroSeiContrato: solicitacaoData.numeroSeiContrato,
          numeroDocumentoSEI: solicitacaoData.numeroDocumentoSEI,
          observacao: solicitacaoData.observacao,
        };

        await createUsuarioVinculo(payload as any);

        CustomSwal.show({
          title: 'Solicitação Enviada',
          html:
            'Sua solicitação de vínculo foi registrada com sucesso. Ela já foi encaminhada para análise do/a ' +
            interfaceLabel +
            ' que você selecionou.',
          type: 'success',
          confirmText: 'OK',
          onConfirm: voltar,
        });
      } catch (error: any) {
        CustomSwal.show({
          title: 'Erro',
          html: error.message || 'Erro ao enviar solicitação.',
          type: 'error',
          confirmText: 'Fechar',
        });
      }
      setSubmitting(false);
      return;
    }

    const missingFields: string[] = [];

    if (!formData.cpf) missingFields.push('CPF');
    if (!formData.senha) missingFields.push('Senha');

    if (missingFields.length > 0) {
      CustomSwal.show({
        title: 'Campos Obrigatórios',
        html: `Por favor, preencha os seguintes campos: ${missingFields.join(', ')}.`,
        type: 'error',
        confirmText: 'Fechar',
      });
      setSubmitting(false);
      return;
    }

    /*if (cpfDigits > 0) {
      //&& !validCpf
      CustomSwal.show({
        title: 'CPF Inválido',
        html: 'O CPF digitado não é válido. Digite novamente.',
        type: 'error',
        confirmText: 'Fechar',
      });
      setFormData({ ...formData, cpf: '' });
      setSubmitting(false);
      return;
    }*/

    try {
      const cpfLimpo = formData.cpf.replace(/\D/g, '');
      const data = await validarUsuarioVinculo({
        cpf: cpfLimpo,
        senha: formData.senha,
      });
      const token = data.access_token || data.token;
      localStorage.setItem('token', token);
      const decoded: any = jwtDecode(token);

      // Carregar domínios para selects antes de avançar
      const dominios = await getDominiosSelect();
      localStorage.setItem('dominios_select', JSON.stringify(dominios));

      if (decoded.possuiVinculo === true) {
        CustomSwal.show({
          title: 'Vínculo Existente',
          html: 'Este usuário já possui um vínculo ativo no sistema.',
          type: 'warning',
          confirmText: 'OK',
          onConfirm: voltar,
        });
        return;
      }

      const pendingRequest = await checkPendingRequest();

      if (pendingRequest.hasPending) {
        CustomSwal.show({
          title: 'Solicitação Pendente',
          html: 'Este usuário já possui uma solicitação de vínculo pendente.',
          type: 'warning',
          confirmText: 'OK',
          onConfirm: voltar,
        });
        return;
      }

      setSolicitacaoData((prev) => ({
        ...prev,
        nome: decoded.nome || decoded.name,
      }));

      setStep(2);
    } catch (error: any) {
      const status = error?.response?.status || error?.status;
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Erro inesperado. Tente novamente.';

      if (status === 401 || status === 403) {
        CustomSwal.show({
          title: 'Dados Inválidos',
          html: 'CPF ou senha incorretos. Verifique seus dados e tente novamente.',
          type: 'error',
          confirmText: 'Fechar',
        });
        return;
      }

      CustomSwal.show({
        title: 'Erro',
        html: errorMessage,
        type: 'error',
        confirmText: 'Fechar',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContainerBase>
      <GlobalStyle />
      <ContainerSolicitacao>
        {/* Lado Esquerdo - Formulário */}
        <SecaoForm>
          <IconLogoContainer>
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                voltar();
              }}
            >
              <span className="material-icons">arrow_back</span>
            </a>
            {/* <img src={logoGov} alt="Logo Governo" /> */}
          </IconLogoContainer>
          <FormContainer
            onSubmit={handleSubmit}
            style={{ width: step === 2 ? '90%' : '50%' }}
          >
            {/* <Title>Formulário de Solicitação de Vínculo</Title> */}
            {step === 1 ? (
              <>
                <Row className="mb-3 justify-content-center">
                  <Col md={6}>
                    <CustomInputMask
                      title="CPF"
                      mask="999.999.999-99"
                      value={formData.cpf}
                      onChange={handleChange}
                      id="CPF"
                      placeholder="CPF"
                      name="cpf"
                      textAlign="center"
                      disabled={submitting}
                      data-testid="input-CPF"
                      required
                    />
                  </Col>
                </Row>
                <Row className="mb-3 justify-content-center">
                  <Col md={6}>
                    <Input
                      name="senha"
                      type="password"
                      value={formData.senha}
                      id="password"
                      label="Senha"
                      placeholder="Informe sua senha"
                      onChange={handleChange}
                      required
                    />
                  </Col>
                </Row>
              </>
            ) : (
              <FormSolicitacao
                formData={solicitacaoData}
                setFormData={setSolicitacaoData}
                disabled={submitting}
              />
            )}

            <ButtonContainer>
              <Button
                label={step === 1 ? 'Verificar' : 'Solicitar Vínculo'}
                type="submit"
                disabled={submitting}
                botaoAzul
              />
            </ButtonContainer>
          </FormContainer>
        </SecaoForm>

        {/* Lado Direito - Imagem */}
        <SecaoImagem>
          <img src={logoSisdaf} alt="Logo SisDAF" />
          <div>
            <span>Solicitar Vínculo</span>
            {step === 1 ? (
              <p>Informe seu CPF e Senha para verificar suas informações.</p>
            ) : (
              <p>
                Preencha os dados para solicitar o vínculo e poder acessar o
                SisAF.
              </p>
            )}
          </div>
        </SecaoImagem>
      </ContainerSolicitacao>
    </ContainerBase>
  );
};

export default SolicitarVinculo;
